import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import CharacterForm from './CharacterForm';
import LoadingSpinner from '../components/LoadingSpinner';
import api from "../api";

function EditCharacter({ loadingBarRef }) {
    const { t } = useTranslation();
    const [characters, setCharacters] = useState([]);
    const [selectedCharacter, setSelectedCharacter] = useState(null);
    const [characterData, setCharacterData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const fetchCharactersList = useCallback(async () => {
        setIsLoading(true);
        if (loadingBarRef.current) loadingBarRef.current.continuousStart();
        try {
            const accessToken = localStorage.getItem('access_token');
            const response = await api.get(`${process.env.REACT_APP_API_URL}/api/characters/list?lang=ru`, {
                headers: { 'Authorization': `Bearer ${accessToken}` }
            });
            setCharacters(response.data);
        } catch (error) {
            await Swal.fire({
                text: t('characters.fetchError'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                confirmButtonText: t('posts.ok'),
                customClass: { popup: 'swal2-dark-popup', confirmButton: 'swal2-dark-button' }
            });
        } finally {
            setIsLoading(false);
            if (loadingBarRef.current) loadingBarRef.current.complete();
        }
    }, [loadingBarRef, t]);

    const fetchCharacterDetails = useCallback(async (id) => {
        setIsLoading(true);
        if (loadingBarRef.current) loadingBarRef.current.continuousStart();
        try {
            const accessToken = localStorage.getItem('access_token');
            const response = await api.get(`${process.env.REACT_APP_API_URL}/api/admin/character/${id}`, {
                headers: { 'Authorization': `Bearer ${accessToken}` }
            });
            setCharacterData({
                ...response.data,
                translations: response.data.translations || {},
                skills: response.data.skills || []
            });
        } catch (error) {
            await Swal.fire({
                text: t('characters.fetchDetailsError'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                confirmButtonText: t('posts.ok'),
                customClass: { popup: 'swal2-dark-popup', confirmButton: 'swal2-dark-button' }
            });
        } finally {
            setIsLoading(false);
            if (loadingBarRef.current) loadingBarRef.current.complete();
        }
    }, [loadingBarRef, t]);

    const handleDeleteCharacter = async (id) => {
        const result = await Swal.fire({
            title: t('characters.deleteConfirmation'),
            text: t('characters.deleteWarning'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('characters.delete'),
            cancelButtonText: t('characters.cancel'),
            background: '#1a1a1a',
            color: '#b3b3b3',
            iconColor: '#ff0000',
            customClass: { popup: 'swal2-dark-popup', confirmButton: 'swal2-dark-button', cancelButton: 'swal2-dark-button' }
        });

        if (result.isConfirmed) {
            setIsLoading(true);
            if (loadingBarRef.current) loadingBarRef.current.continuousStart();
            try {
                const accessToken = localStorage.getItem('access_token');
                await api.delete(`${process.env.REACT_APP_API_URL}/api/admin/character/${id}`, {
                    headers: { 'Authorization': `Bearer ${accessToken}` }
                });
                await Swal.fire({
                    text: t('characters.deleteSuccess'),
                    icon: 'success',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#00ff00',
                    confirmButtonText: t('posts.ok'),
                    customClass: { popup: 'swal2-dark-popup', confirmButton: 'swal2-dark-button' }
                });
                await fetchCharactersList();
            } catch (error) {
                await Swal.fire({
                    text: t('characters.deleteError'),
                    icon: 'error',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#ff0000',
                    confirmButtonText: t('posts.ok'),
                    customClass: { popup: 'swal2-dark-popup', confirmButton: 'swal2-dark-button' }
                });
            } finally {
                setIsLoading(false);
                if (loadingBarRef.current) loadingBarRef.current.complete();
            }
        }
    };

    useEffect(() => {
        fetchCharactersList();
    }, [fetchCharactersList]);

    const handleSelectCharacter = async (character) => {
        setSelectedCharacter(character);
        await fetchCharacterDetails(character.id);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        if (loadingBarRef.current) loadingBarRef.current.continuousStart();
        try {
            const accessToken = localStorage.getItem('access_token');
            const translationsArray = Object.entries(characterData.translations).map(([lang, trans]) => ({
                language_code: lang,
                ...trans
            }));
            const dataToSend = {
                ...characterData,
                translations: translationsArray,
                skills: characterData.skills
            };
            const response = await api.put(`${process.env.REACT_APP_API_URL}/api/admin/character/${selectedCharacter.id}`, dataToSend, {
                headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' }
            });
            if (response.status === 200) {
                await Swal.fire({
                    text: t('characters.updateSuccess'),
                    icon: 'success',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#00ff00',
                    confirmButtonText: t('posts.ok'),
                    customClass: { popup: 'swal2-dark-popup', confirmButton: 'swal2-dark-button' }
                });
                setSelectedCharacter(null);
                setCharacterData(null);
                await fetchCharactersList();
            }
        } catch (error) {
            await Swal.fire({
                text: t('characters.updateError'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                confirmButtonText: t('posts.ok'),
                customClass: { popup: 'swal2-dark-popup', confirmButton: 'swal2-dark-button' }
            });
        } finally {
            setIsSubmitting(false);
            if (loadingBarRef.current) loadingBarRef.current.complete();
        }
    };

    return (
        <div>
            {isLoading ? (
                <div className="text-center">
                    <LoadingSpinner />
                    <p>{t('characters.loading')}</p>
                </div>
            ) : selectedCharacter ? (
                characterData ? (
                    <CharacterForm
                        characterData={characterData}
                        setCharacterData={setCharacterData}
                        onSubmit={handleSubmit}
                        isCreating={false}
                        isSubmitting={isSubmitting}
                        onClose={() => setSelectedCharacter(null)}
                        loadingBarRef={loadingBarRef}
                    />
                ) : (
                    <p className="text-center">{t('characters.loadingDetails')}</p>
                )
            ) : (
                <div>
                    <h3 className="mb-4">{t('characters.listTitle')}</h3>
                    <Table striped bordered hover className="mb-4" id="edit-character-form">
                        <thead>
                        <tr>
                            <th>{t('characters.id')}</th>
                            <th>{t('characters.name')}</th>
                            <th>{t('characters.actions')}</th>
                        </tr>
                        </thead>
                        <tbody>
                        {characters.map((character) => (
                            <tr key={character.id}>
                                <td>{character.id}</td>
                                <td>{character.name}</td>
                                <td>
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={() => handleSelectCharacter(character)}
                                        className="me-2"
                                    >
                                        {t('characters.edit')}
                                    </Button>
                                    <Button
                                        variant="danger"
                                        size="sm"
                                        onClick={() => handleDeleteCharacter(character.id)}
                                    >
                                        {t('characters.delete')}
                                    </Button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </Table>
                </div>
            )}
        </div>
    );
}

export default EditCharacter;